import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'

import { istReloadAngestossen } from '@/utils/chunkLadefehler'
import logging from '@/utils/logging.ts'

/** Bei folgenden Components wird die Performance gemessen wie schnell sie
 *  erstellt, gemounted, aktualisiert, etc. wird  */
const COMPONENTS_TO_TRACE: string[] = [
  'App',
  'RouterView',
  'BaseAppLayout',
  'VAppBar',
  'VNavigationDrawer',
  'BaseNavigation',
  'Dashboard',
  'AsiXLayoutList',
  'AsiXLayoutMatrix',
  'BaseKendoGrid',
  'BaseAgGrid',
  'BaseAgGridMatrix',
  'AsiXLayoutMap',
  // Forms
  'AsiXLayoutForm',
  'BaseLayoutForm',
  'BaseForm',
  // FormCards
  'BaseLayoutFormCardList',
  'BaseLayoutFormCard',
]

/**
 * Ziele, an die Sentry die Tracing-Header `sentry-trace` und `baggage` weitergibt.
 *
 * Sentry prüft Zeichenketten per `includes()` auf der absolut aufgelösten URL.
 * Das bisherige Muster `'asix.pro'` erfasste damit auch `status.asix.pro`; der
 * ilert-Statusdienst (siehe `useIlertStatus`) erlaubt `sentry-trace` aber nicht in
 * `Access-Control-Allow-Headers` und der CORS-Preflight scheitert. Darum hier
 * Regex-Muster mit ausdrücklichem Ausschluss des Statusdienstes.
 *
 * - `/^\//` deckt alle Same-Origin-Requests ab (eigenes Backend, Medien, Statics).
 *   Weil `VITE_APP_BACKEND_URL` leer ist, laufen alle API-Aufrufe relativ über
 *   `/api/v1` – das Muster gilt damit für jede Deployment-Domain, auch für die
 *   DevStack-Worktrees wie `asix.wN.devstack.asix.pro`.
 * - Die Host-Muster erfassen die Cross-Origin-Dienste (z. B. den Excel-Export
 *   unter `export.asix.pro`), deren Traces bewusst weitergeführt werden.
 */
export const TRACE_PROPAGATION_TARGETS: (string | RegExp)[] = [
  /^\//,
  /^https?:\/\/(?!status\.asix\.pro(?:[:/]|$))(?:[a-z0-9-]+\.)*asix\.pro(?:[:/]|$)/,
  /^https?:\/\/(?:[a-z0-9-]+\.)*drschaer\.com(?:[:/]|$)/,
  /^https?:\/\/(?:[a-z0-9-]+\.)*asix\.elpo\.eu(?:[:/]|$)/,
]

export const SENTRY_FINGERPRINT_CHUNK_LADEFEHLER = 'chunk-ladefehler'

// Meldungstexte von Vite bzw. der Browser (Chrome, Firefox, Safari) für fehlgeschlagene dynamische Imports
const CHUNK_LADEFEHLER_MUSTER = [
  'Unable to preload CSS',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
]

/**
 * Chunk-Ladefehler nach Deploys tragen Chunk-Name und Hash in der Meldung; ohne festen Fingerprint entsteht pro
 * Deploy und Instanz ein neues Issue. Während des automatischen Reloads entstehen Folgefehler (z.B. vue-router
 * „Couldn't resolve component“), die nicht gemeldet werden. asix/organisation#14573
 */
export function filtereChunkLadefehler(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (istReloadAngestossen()) {
    return null
  }
  const istChunkLadefehler = event.exception?.values?.some(({ value }) =>
    !!value && CHUNK_LADEFEHLER_MUSTER.some((muster) => value.includes(muster)),
  )
  if (istChunkLadefehler) {
    event.fingerprint = [SENTRY_FINGERPRINT_CHUNK_LADEFEHLER]
  }
  return event
}

// Wird in BaseLayoutForm.vue für ausgewählte User gesetzt, um bei diesen immer 100% aller Metriken sammeln
export let deepSentryMonitoring = false
export function processLoginSentry(loginData: { user_email: string, username: string}): void {
  const monitoredUsers = ['anna.g@asix.pro', 'felix.w@asix.pro']
  Sentry.setUser({
    email: loginData.user_email,
    username: loginData.username,
  })
  if (monitoredUsers.includes(loginData.username)) {
    deepSentryMonitoring = true
    Sentry.getReplay()?.start()
    Sentry.uiProfiler.startProfiler()
  } else if (Math.random() < 0.01) {
    // enable Profiler for 1% of users anyways
    Sentry.uiProfiler.startProfiler()
  }
}
export function processLogoutSentry() {
  Sentry.setUser(null)
  deepSentryMonitoring = false
}

export default function initSentry(app: App<Element>, router: Router) {
  if (['production', 'projekt'].includes(import.meta.env.MODE)) {
    Sentry.init({
      app,
      environment: import.meta.env.MODE || 'development',
      release: import.meta.env.VITE_APP_VERSION,
      dsn: 'https://79bb179874754ea9a737478a1e959da7@sentry.asix.pro/3',
      attachStacktrace: true,
      sampleRate: 1.0,
      debug: false,
      dataCollection: {
        userInfo: true,
      },

      // This sets the sample rate to be 10%. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: 0.01,
      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: 1,

      tracesSampler: ({ inheritOrSampleWith }) => {
        if (deepSentryMonitoring) {
          // Ausgewählte User mit Problemen immer tracen
          return 1.0
        }

        return inheritOrSampleWith(0.1)
      },
      // https://docs.sentry.io/concepts/key-terms/tracing/distributed-tracing/
      tracePropagationTargets: TRACE_PROPAGATION_TARGETS,
      beforeSend: filtereChunkLadefehler,

      // Every session is technically eligible for profiling,
      // but we manually start it only for selected users after Login (see BaseLoginForm.vue).
      profileSessionSampleRate: 1.0,
      profileLifecycle: "manual",


      integrations: [
        Sentry.vueIntegration({
          tracingOptions: {
            trackComponents: COMPONENTS_TO_TRACE,
            hooks: ["create", "activate", "mount", "update", "unmount"],
          }
        }),
        Sentry.replayIntegration({
          // Additional SDK configuration goes in here, for example:
          maskAllText: false,
          blockAllMedia: false
        }),
        Sentry.replayCanvasIntegration(),
        Sentry.captureConsoleIntegration({
          levels: ['error', 'warning']
        }),
        Sentry.browserSessionIntegration(),
        Sentry.browserTracingIntegration({
          router,
        }),
        Sentry.browserProfilingIntegration()
      ],
    })
    logging.debug('Initialized Sentry')

    function getAppMode() {
      const nav = navigator as Navigator & {
        standalone?: boolean
      }
      if (nav.standalone) {
        return 'ios_standalone'
      }
      if (window.matchMedia("(display-mode: standalone)").matches) {
        return "standalone"
      }
      return 'browser'
    }
    // Standalone-Modus (AsiX direkt als App-Icon auf dem Home Screen) taggen, damit in Sentry nach spezifischen
    // Performance-Problemen gesucht werden kann
    Sentry.setTag("app_mode", getAppMode())
  }
}